//Route.js
import React, {  useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from "../../../services/api.js";
import styles from './Route.module.css';
import filterStyles from './FilterMenu.module.css';

import BottomNav from "../../../components/BottomNav/BottomNav.js";
import RouteInfo from "./RouteInfo/Routeinfo.js";
import ControlPanel from './ControlPanel/ControlPanel'
import ReceiptInfo from "./ReceiptInfo/ReceiptInfo.js";

import CourierModal from "../CourierModal/CourierModal";


import useRouteHandlers from "./RouteHandlers";
import { useRouteStore } from './useRouteStore';

  // Утилита расчёта прогресса
const getProgress = (receipts) => {
  if (!Array.isArray(receipts) || receipts.length === 0) {
    return { delivered: 0, inProgress: 0, undelivered: 0, totalOrders: 0 };
  }
  const totalOrders  = receipts.length;
  const delivered    = receipts.filter(r => r.status === 'Закрыта').length;
  const inProgress   = receipts.filter(r => r.status === 'Открыта').length;
  const undelivered  = receipts.filter(r => r.status === 'Не доставлено').length;
  return { delivered, inProgress, undelivered, totalOrders };
};




const Route = () => {
  // 1️⃣ Получаем routeId и navigate
  const { id: routeId } = useParams();
  const navigate    = useNavigate();
  const setRouteData = useRouteStore(s => s.setRouteData);
  const queryClient = useQueryClient();

  // Запускаем запрос
  const { data: route, isLoading, error } = useQuery({
    queryKey: ['route', routeId],
    queryFn: () => api.get(`/routes/${routeId}`),
    enabled: Boolean(routeId),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    retry: 1,
  });


  // В useEffect спокойно пишем результат в Zustand
  useEffect(() => {
    if (route) {
      setRouteData(route);
    }
  }, [route, setRouteData]);

    // **Логи сразу после вызова стора**
  const storeRouteData = useRouteStore(s => s.routeData);


  // 5️⃣ Zustand: всё UI-состояние и экшены
  const {
    // фильтр и поиск
    searchQuery,        setSearchQuery,
    grouped,            toggleGrouping,
    filterStatus,       setFilterStatus,
    sortOrder,          
    isSearchVisible,    toggleSearch,
    showFilterMenu,     toggleFilterMenu,
    allExpanded,        toggleExpandAll,

  // … фильтры, группировка и т.д. …
  selectedReceiptId, setSelectedReceiptId,
  modalPage,
  setModalPage
  } = useRouteStore();

  // 6️⃣ Хук бизнес-логики и навигации
    const {
    handleGoBack,
    handleMap,
    handleSearchSubmit,
    /* убрали handleSortAddress */
    /* ... */
  } = useRouteHandlers({ routeId, navigate });

  // добавляем прямо из стора
  const onSortAddress = useRouteStore(s => s.toggleSortOrder);

  // 8️⃣ Рендер до загрузки / при ошибке
  if (isLoading) return <div className="route-explorer">Загрузка...</div>;
  if (error)     return <div className="route-explorer">Ошибка загрузки</div>;
  if (!route)    return <div className="route-explorer">Загрузка...</div>;

  // 9️⃣ Фильтрация и сортировка квитанций
  const receipts = storeRouteData?.receipts ?? [];
  let receiptsToDisplay = receipts
    .filter(r => filterStatus === "Все" || r.status === filterStatus)
    .slice();

  const hasSequence = receiptsToDisplay.every(r => typeof r.sequence_number === 'number');

  if (hasSequence && sortOrder === 'none') {
    // сортируем по порядковому номеру, если он есть и сортировка по адресу не активна
    receiptsToDisplay.sort((a, b) => a.sequence_number - b.sequence_number);
  } else {
    // сортируем по адресу в зависимости от sortOrder
    receiptsToDisplay.sort((a, b) => {
      const addrA = (a.delivery_address || "").trim();
      const addrB = (b.delivery_address || "").trim();
      if (sortOrder === 'asc')  return addrA.localeCompare(addrB);
      if (sortOrder === 'desc') return addrB.localeCompare(addrA);
      return 0; // при sortOrder === 'none' — оставляем исходный порядок
    });
  }
  switch (sortOrder) {
  case 'sequence':
    receiptsToDisplay.sort((a,b) => (a.sequence_number ?? Infinity) - (b.sequence_number ?? Infinity));
    break;
  case 'addressAsc':
    receiptsToDisplay.sort((a,b) =>
      (a.delivery_address||'').localeCompare(b.delivery_address||'')
    );
    break;
  case 'addressDesc':
    receiptsToDisplay.sort((a,b) =>
      (b.delivery_address||'').localeCompare(a.delivery_address||'')
    );
    break;
  case 'id':
    receiptsToDisplay.sort((a,b) => a.id - b.id);
    break;
  default:
    break;
}



  const { delivered, inProgress, undelivered, totalOrders } =
    getProgress(route.receipts);

  // — считаем, где чек нужно пробивать
  const notNeededTypes = ["ПоСчету", "Кредит", "Взаимозачет", "Дилер", "БезОплаты"];
  const eligibleForCheck = route.receipts.filter(r =>
    !notNeededTypes.includes(r.payment_type) && r.status !== "Не доставлено"
  );
  const checksIssuedCount = eligibleForCheck.filter(r => r.is_check_issued).length;
  const eligibleCount = eligibleForCheck.length;

  const deliveredWidth   = totalOrders > 0 ? (delivered   / totalOrders) * 100 : 0;
  const inProgressWidth  = totalOrders > 0 ? (inProgress  / totalOrders) * 100 : 0;
  const undeliveredWidth = totalOrders > 0 ? (undelivered / totalOrders) * 100 : 0;

  return (
    <div className={styles.routePageContainer}>
      {/* Информационный блок о маршруте */}
      <RouteInfo route={route} />

      {/* Панель управления */}
      <ControlPanel
        grouped={grouped}
        toggleGrouping={toggleGrouping}
        sortOrder={sortOrder}
        onSortAddress={onSortAddress}
        allExpanded={allExpanded}
        toggleExpandAll={toggleExpandAll}
      />

      {/* Фильтр и поиск */}
      {showFilterMenu && (
        <>
          {/* фоновый слой: ловит клик, закрывает меню */}
          <div
            className={filterStyles.backdrop}
            onClick={toggleFilterMenu}
          />

          {/* само окно фильтра */}
          <div className={filterStyles.overlay}>
            {['Все','Открыта','Закрыта','Не доставлено'].map(status => {
              const isActive = filterStatus === status;
              return (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`${filterStyles.item} ${isActive ? filterStyles.active : ''}`}
                >
                  {status} (
                    {route.receipts.filter(r => status === 'Все' || r.status === status).length}
                  )
                </button>
              );
            })}
          </div>
          
        </>
      )}
      {isSearchVisible && (
        <form className={styles.searchForm} onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Поиск по квитанциям..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.searchButton}>Искать</button>
        </form>
      )}

      <div className={styles.receiptsContainer}>
        {grouped ? (
          Object.entries(
            receiptsToDisplay.reduce((acc, receipt) => {
              const key = receipt.delivery_address?.trim() || 'Без адреса';
              acc[key] = acc[key] || [];
              acc[key].push(receipt);
              return acc;
            }, {})
          ).map(([groupName, receipts]) => (
            <div key={groupName} className={styles.groupContainer}>
              <h3 className={styles.groupTitle}>{groupName}</h3>
              {receipts.map(receipt => (
                <div key={receipt.id + receipt.updatedAt}>
                  <ReceiptInfo
                    receipt={receipt}
                    allExpanded={allExpanded}
                    navigate={navigate}
                  />
                </div>
              ))}
            </div>
          ))
        ) : (
          receiptsToDisplay.map(receipt => (
            <div key={receipt.id + receipt.updatedAt}>
              <ReceiptInfo
                receipt={receipt}
                allExpanded={allExpanded}
                navigate={navigate}
              />
            </div>
          ))
        )}
        {receiptsToDisplay.length === 0 && (
          <p className={styles.noReceipts}>Нет квитанций по заданным фильтрам</p>
        )}
      </div>

      <CourierModal
        isOpen={modalPage !== null}
        onClose={() => setModalPage(null)}
        initialPage={modalPage}
        receiptId={selectedReceiptId}
        routeId={routeId}
      />

      <BottomNav
        toggleSearch={toggleSearch}
        toggleFilter={toggleFilterMenu}
        goToRoutes={() => navigate(-1)}
        goToMap={() => navigate(`/map/${routeId}`)}
      />
    </div>
  );
}

export default Route;
